import { apiFetch } from '../../../utils/apiClient';
import { listLscArchive, getLscArchiveItem } from '../../../lsc/api';
import { isKnownAcquireErrorCode } from '../lib/mapAcquireError';
import { gateFieldsFromArchive } from '../lib/normalizeGateFields';
import type {
  RttsAcquireFailure,
  RttsAcquireRequest,
  RttsAcquireResult,
  RttsGateFields,
  RttsJobDetail,
} from '../types';

/**
 * Live RTTS client.
 *
 * Gate fields: read from the existing league archive object (PR #143) —
 * GET /api/lsc/leagues/{league_id}/archive[/{archive_id}]
 * // BACKEND-DEP: confirm exact archive endpoint path if it differs from the contract's inferred route.
 *
 * Acquire / job status (confirmed against backend highlight_routes):
 *   POST /api/lsc/leagues/{league_id}/highlights/acquire
 *   GET  /api/lsc/leagues/{league_id}/highlights/jobs/{job_id}
 */

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function normalizeJobDetail(raw: unknown): RttsJobDetail {
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const id = String(record.id ?? record.job_id ?? '').trim();
  return {
    id,
    status: String(record.status ?? ''),
    signed_url:
      typeof record.signed_url === 'string' ? record.signed_url : null,
    expires_at:
      typeof record.expires_at === 'string' ? record.expires_at : null,
    error_code:
      typeof record.error_code === 'string' ? record.error_code : null,
    reel_duration_sec:
      typeof record.reel_duration_sec === 'number'
        ? record.reel_duration_sec
        : null,
    // BACKEND-DEP: richer share surface (og image / share payload)
    share_title:
      typeof record.share_title === 'string' ? record.share_title : null,
    share_thumbnail_url:
      typeof record.poster_url === 'string'
        ? record.poster_url
        : typeof record.share_thumbnail_url === 'string'
          ? record.share_thumbnail_url
          : null,
    share_url: typeof record.share_url === 'string' ? record.share_url : null,
  };
}

/** List finished/archived games with gate fields from the live archive list. */
export async function liveListRttsGames(leagueId: string): Promise<RttsGateFields[]> {
  // Prefer published archives for the RTTS tab (member-visible).
  const items = await listLscArchive(leagueId, {
    archive_status: 'published',
    limit: 100,
  });
  return items.map((archive) => gateFieldsFromArchive(archive));
}

/** Gate fields for a single archive via the live archive detail endpoint. */
export async function liveGetRttsGateFields(
  leagueId: string,
  archiveId: string
): Promise<RttsGateFields> {
  const archive = await getLscArchiveItem(leagueId, archiveId);
  return gateFieldsFromArchive(archive);
}

function optionalFiniteInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.trunc(parsed);
  }
  return undefined;
}

/** Parse a typed acquire failure from the JSON body (key off `error` string). */
export function parseAcquireFailurePayload(
  payload: unknown,
  status: number
): RttsAcquireFailure {
  const record =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};
  const rawError = typeof record.error === 'string' ? record.error.trim() : '';
  const error = isKnownAcquireErrorCode(rawError) ? rawError : 'unknown';
  const failure: RttsAcquireFailure = {
    success: false,
    error,
    status,
  };
  if (typeof record.message === 'string' && record.message.trim()) {
    failure.message = record.message.trim();
  }
  const maxMinutes = optionalFiniteInt(record.max_minutes);
  const vodMinutes = optionalFiniteInt(record.vod_minutes);
  if (maxMinutes != null) failure.max_minutes = maxMinutes;
  if (vodMinutes != null) failure.vod_minutes = vodMinutes;
  return failure;
}

export async function liveAcquireHighlight(
  leagueId: string,
  body: RttsAcquireRequest
): Promise<RttsAcquireResult> {
  const response = await apiFetch(
    `/api/lsc/leagues/${encodeURIComponent(leagueId)}/highlights/acquire`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        archive_id: body.archive_id,
        force: Boolean(body.force),
      }),
    }
  );

  const payload = await readJson(response);

  if (!response.ok) {
    // 5xx → throw so the button shows generic copy + Retry.
    // Typed 4xx (403/409/422/429/…) → return body.error for mapAcquireError.
    if (response.status >= 500) {
      const message =
        payload &&
        typeof payload === 'object' &&
        typeof (payload as { message?: unknown }).message === 'string'
          ? String((payload as { message: string }).message)
          : `Acquire failed (${response.status})`;
      throw new Error(message);
    }
    return parseAcquireFailurePayload(payload, response.status);
  }

  const successPayload = payload as
    | { success?: boolean; job?: unknown }
    | null;

  if (successPayload && successPayload.success === true) {
    const job = normalizeJobDetail(successPayload.job);
    if (!job.id) {
      throw new Error('Acquire returned a job without an id');
    }
    return { success: true, job: { id: job.id, status: job.status || 'downloading' } };
  }

  throw new Error('Acquire returned an unexpected response');
}

/**
 * Fetch member-safe job status (and signed_url when delivered).
 * // BACKEND-DEP: confirm whether a dedicated URL-refresh endpoint exists; for now re-fetching the job is the MVP path.
 */
export async function liveGetHighlightJob(
  leagueId: string,
  jobId: string
): Promise<RttsJobDetail> {
  const response = await apiFetch(
    `/api/lsc/leagues/${encodeURIComponent(leagueId)}/highlights/jobs/${encodeURIComponent(jobId)}`
  );
  if (!response.ok) {
    const body = (await readJson(response)) as { message?: string; error?: string } | null;
    throw new Error(
      body?.message || body?.error || `Failed to load highlight job (${response.status})`
    );
  }
  const body = (await readJson(response)) as
    | RttsJobDetail
    | { job?: unknown; success?: boolean }
    | null;
  if (body && typeof body === 'object' && 'job' in body && body.job) {
    return normalizeJobDetail(body.job);
  }
  return normalizeJobDetail(body);
}
